import { useEffect, useState } from "react";
import { useLang } from "@/lib/lang";
import {
  generateCaseQuestions,
  skipCaseOnboarding,
  submitCaseAnswers,
  updateCase,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

function emptyAnswerState() {
  return { selectedOptionId: "", details: "" };
}

function isMcqQuestion(q) {
  return Array.isArray(q?.options) && q.options.length > 0;
}

export default function CaseKnowledgePane({ caseData, model, onUpdated }) {
  const { t } = useLang();
  const [knowledgeMd, setKnowledgeMd] = useState(caseData?.knowledge_md || "");
  const [skills, setSkills] = useState(caseData?.skills || []);
  const [questions, setQuestions] = useState(caseData?.onboarding_questions || []);
  const [answers, setAnswers] = useState({});
  const [generating, setGenerating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [saving, setSaving] = useState(false);

  const onboardingStatus = caseData?.onboarding_status || "pending";
  const showBanner = onboardingStatus === "pending" || onboardingStatus === "in_progress";

  useEffect(() => {
    setKnowledgeMd(caseData?.knowledge_md || "");
    setSkills(caseData?.skills || []);
    setQuestions(caseData?.onboarding_questions || []);
    const initial = {};
    (caseData?.onboarding_questions || []).forEach((q) => {
      initial[q.id] = {
        selectedOptionId: q.selected_option_id || "",
        details: q.answer_details || "",
        legacyText: q.answer || "",
      };
    });
    setAnswers(initial);
  }, [caseData?.id, caseData?.knowledge_md, caseData?.skills, caseData?.onboarding_questions, caseData?.onboarding_status]);

  const setAnswerField = (questionId, field, value) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: { ...emptyAnswerState(), ...prev[questionId], [field]: value },
    }));
  };

  const handleGenerateQuestions = async () => {
    setGenerating(true);
    try {
      const data = await generateCaseQuestions(caseData.id, model);
      setQuestions(data.questions || []);
      onUpdated?.();
    } catch {
      toast.error(t("case_questions_error"));
    } finally {
      setGenerating(false);
    }
  };

  const handleSkip = async () => {
    try {
      await skipCaseOnboarding(caseData.id);
      onUpdated?.();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleSubmitAnswers = async () => {
    const payload = questions
      .map((q) => {
        const state = answers[q.id] || emptyAnswerState();
        if (isMcqQuestion(q)) {
          if (!state.selectedOptionId && !state.details?.trim()) return null;
          return {
            id: q.id,
            selected_option_id: state.selectedOptionId || undefined,
            answer_details: state.details?.trim() || undefined,
          };
        }
        const text = (state.legacyText || state.details || "").trim();
        if (!text) return null;
        return { id: q.id, answer: text };
      })
      .filter(Boolean);

    if (!payload.length) {
      toast.error(t("case_onboarding_select_option"));
      return;
    }

    const mcqQuestions = questions.filter(isMcqQuestion);
    const missingSelection = mcqQuestions.some((q) => !(answers[q.id]?.selectedOptionId));
    if (missingSelection) {
      toast.error(t("case_onboarding_select_option"));
      return;
    }

    setSubmitting(true);
    try {
      const data = await submitCaseAnswers(caseData.id, payload, model);
      setKnowledgeMd(data.knowledge_md || "");
      setSkills(data.skills || []);
      onUpdated?.();
      toast.success(t("case_saved"));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveManual = async () => {
    setSaving(true);
    try {
      await updateCase(caseData.id, { knowledge_md: knowledgeMd, skills });
      toast.success(t("case_saved"));
      onUpdated?.();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const addSkill = () => {
    setSkills((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        title: "",
        content: "",
        source: "user",
        updated_at: new Date().toISOString(),
      },
    ]);
  };

  const updateSkill = (id, field, value) => {
    setSkills((prev) =>
      prev.map((s) => (s.id === id ? { ...s, [field]: value } : s)),
    );
  };

  const removeSkill = (id) => {
    setSkills((prev) => prev.filter((s) => s.id !== id));
  };

  return (
    <div className="h-full flex flex-col bg-white overflow-hidden" data-testid="case-knowledge-pane">
      <div className="grid-border-b p-4 bg-slate-50/40 flex items-center justify-between shrink-0">
        <h3 className="font-serif-display text-base text-slate-900">{t("case_knowledge")}</h3>
        <Button
          size="sm"
          className="rounded-none h-8 text-xs bg-slate-900 text-white"
          onClick={handleSaveManual}
          disabled={saving}
        >
          {t("case_save")}
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {showBanner && (
          <div className="grid-border bg-amber-50/80 p-4 space-y-3">
            <p className="text-sm text-slate-800">{t("case_onboarding_banner")}</p>
            <div className="flex gap-2 flex-wrap">
              <Button
                size="sm"
                className="rounded-none bg-slate-900 text-white h-8 text-xs"
                onClick={handleGenerateQuestions}
                disabled={generating}
              >
                {generating ? (
                  <Loader2 size={14} className="animate-spin me-1" />
                ) : null}
                {generating ? t("case_questions_loading") : t("case_onboarding_start")}
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="rounded-none h-8 text-xs"
                onClick={handleSkip}
              >
                {t("case_onboarding_skip")}
              </Button>
            </div>
          </div>
        )}

        {questions.length > 0 && onboardingStatus !== "completed" && (
          <div className="grid-border p-4 space-y-5">
            {questions.map((q) => {
              const state = answers[q.id] || emptyAnswerState();
              return (
                <div key={q.id} className="space-y-3" data-testid={`onboarding-q-${q.id}`}>
                  <div className="text-sm font-medium text-slate-900">{q.question}</div>

                  {isMcqQuestion(q) ? (
                    <>
                      <div className="space-y-2" role="radiogroup" aria-label={q.question}>
                        {q.options.map((opt) => {
                          const selected = state.selectedOptionId === opt.id;
                          return (
                            <label
                              key={opt.id}
                              className={[
                                "flex items-start gap-3 p-3 grid-border cursor-pointer transition-colors",
                                selected ? "bg-slate-900 text-white" : "bg-white hover:bg-slate-50",
                              ].join(" ")}
                            >
                              <input
                                type="radio"
                                name={`onboarding-${q.id}`}
                                value={opt.id}
                                checked={selected}
                                onChange={() => setAnswerField(q.id, "selectedOptionId", opt.id)}
                                className="mt-0.5 shrink-0"
                              />
                              <span className="text-sm leading-relaxed">{opt.label}</span>
                            </label>
                          );
                        })}
                      </div>
                      <div>
                        <label className="eyebrow block mb-1.5">{t("case_onboarding_details")}</label>
                        <Textarea
                          value={state.details || ""}
                          onChange={(e) => setAnswerField(q.id, "details", e.target.value)}
                          rows={2}
                          placeholder={t("case_onboarding_details_placeholder")}
                          className="rounded-none grid-border text-sm resize-none"
                        />
                      </div>
                    </>
                  ) : (
                    <Textarea
                      value={state.legacyText || ""}
                      onChange={(e) => setAnswerField(q.id, "legacyText", e.target.value)}
                      rows={2}
                      className="rounded-none grid-border text-sm resize-none"
                    />
                  )}
                </div>
              );
            })}
            <Button
              className="rounded-none bg-slate-900 text-white w-full"
              onClick={handleSubmitAnswers}
              disabled={submitting}
            >
              {t("case_onboarding_submit")}
            </Button>
          </div>
        )}

        <div>
          <label className="eyebrow block mb-2">{t("case_knowledge")}</label>
          <Textarea
            value={knowledgeMd}
            onChange={(e) => setKnowledgeMd(e.target.value)}
            rows={8}
            placeholder={t("case_knowledge_placeholder")}
            className="rounded-none grid-border text-sm resize-none font-mono-tight"
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="eyebrow">{t("case_knowledge")} — skills</span>
            <button
              type="button"
              onClick={addSkill}
              className="flex items-center gap-1 text-[10px] text-slate-600 hover:text-slate-900"
            >
              <Plus size={12} />
              {t("case_skills_add")}
            </button>
          </div>
          <div className="space-y-3">
            {skills.map((skill) => (
              <div key={skill.id} className="grid-border p-3 space-y-2">
                <div className="flex gap-2">
                  <input
                    value={skill.title || ""}
                    onChange={(e) => updateSkill(skill.id, "title", e.target.value)}
                    placeholder={t("case_skill_title")}
                    className="flex-1 grid-border px-2 py-1 text-xs outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => removeSkill(skill.id)}
                    className="p-1 text-red-600 hover:bg-red-50"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
                <Textarea
                  value={skill.content || ""}
                  onChange={(e) => updateSkill(skill.id, "content", e.target.value)}
                  placeholder={t("case_skill_content")}
                  rows={2}
                  className="rounded-none grid-border text-xs resize-none"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
